import Typography from "antd/lib/typography";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { useTranslation } from "react-i18next";

import { useActiveClassroom } from "@sol/classrooms";
import { Feature, FeatureGuard } from "@sol/features";
import { interpolate, Route } from "@sol/routing";
import { getResourceUuid, IClassroom, IClassroomResource } from "@sol/sdk";
import { Calendar as CalendarIcon } from "@sol/uikit/core/icons/Calendar";
import { Pencil } from "@sol/uikit/core/icons/Pencil";
import { Pin } from "@sol/uikit/core/icons/Pin";
import { Users } from "@sol/uikit/core/icons/Users";
import { PlaceholderPicture } from "@sol/uikit/data-display/PlaceholderPicture";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";
import { Button } from "@sol/uikit/general/Button/Button";

import { isClassroomWebinarAvailable, OpenClassroomWebinarButton } from "@/features/open-classroom-webinar";

import { CLASSROOM_CARD_COVER_WIDTH } from "../../modules/uikit/theme/imageSizes";

const LoadingClassroomCardContent = ({ className }: { className?: string }) => (
    <div className={clsx("grid w-full grid-flow-row auto-rows-max grid-cols-12 gap-ant-md", className)}>
        <div className="col-span-6 min-w-0">
            <Skeleton.Node className="size-full!" active />
        </div>
        <div className="col-span-6 min-w-0">
            <div className="flex h-full flex-col justify-between">
                <div className="mb-ant-lg">
                    <Skeleton title={{ width: "70%" }} paragraph={false} className="mb-ant-lg" active />
                    <div>
                        <Skeleton paragraph={{ rows: 4 }} title={false} active />
                    </div>
                </div>
                <div className="flex flex-col justify-between">
                    <ul className="grid list-none grid-flow-row auto-rows-max grid-cols-12 gap-ant-md p-0">
                        {[1, 2, 3].map(index => (
                            <li className="col-span-4 flex min-w-0 items-center" key={index}>
                                <Skeleton
                                    avatar={{ size: 24, shape: "circle" }}
                                    paragraph={false}
                                    className="mr-ant-xs flex items-center [&_h3]:mt-0!"
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    </div>
);

type Props = {
    classroom?: IClassroomResource & Pick<IClassroom, "image">;
    showVisio?: boolean;
    className?: string;
};

const ClassroomCardContent = ({ classroom, showVisio, className }: Props) => {
    const [t] = useTranslation();
    const { isLoading: isLoadingClassroom } = useActiveClassroom();

    if (isLoadingClassroom || !classroom) {
        return <LoadingClassroomCardContent className={className} />;
    }

    const { "@id": id, title, image, description, startDate, endDate, location, nbLearners } = classroom;

    return (
        <>
            <div className={clsx("grid w-full grid-flow-row auto-rows-max grid-cols-12 gap-ant-md", className)}>
                <div className="col-span-6 min-w-0">
                    <div className="relative flex h-[290px] w-full">
                        {image?.url ? (
                            <Image
                                src={image.url}
                                width={CLASSROOM_CARD_COVER_WIDTH}
                                height={290}
                                layout="intrinsic"
                                alt=""
                                objectFit="cover"
                                objectPosition="center"
                                className="rounded-ant-lg"
                            />
                        ) : (
                            <PlaceholderPicture
                                seed={getResourceUuid(classroom)}
                                className="block h-[290px] w-full rounded-ant-lg"
                            />
                        )}
                    </div>
                </div>
                <div className="col-span-6 min-w-0">
                    <div className="flex h-full flex-col justify-between">
                        <div className="mb-ant-lg">
                            <Typography.Title
                                level={2}
                                className="text-ant-h3"
                                ellipsis={{ rows: 2, tooltip: { title: title } }}
                                data-cy="classroom-card-title"
                            >
                                {title}
                            </Typography.Title>
                            <Typography.Paragraph
                                className="mt-7"
                                ellipsis={{ rows: 5, tooltip: { title: description } }}
                            >
                                {description}
                            </Typography.Paragraph>
                        </div>
                        <div className="flex flex-col justify-between">
                            <ul className="grid list-none grid-flow-row auto-rows-max grid-cols-12 gap-ant-md p-0">
                                {location && (
                                    <li className="col-span-4 flex min-w-0 items-center gap-ant-xs">
                                        <Pin className="text-primary" />
                                        <Typography.Text className="font-ant-strong">{location.title}</Typography.Text>
                                    </li>
                                )}
                                <li
                                    className={clsx(
                                        "col-span-4 flex min-w-0 items-center gap-ant-xs",
                                        location ? "col-span-4" : "col-span-6",
                                    )}
                                >
                                    <Users className="text-primary" />
                                    <Typography.Text className="font-ant-strong">
                                        {t("page.classrooms.home.learners.totalLearners", {
                                            count: nbLearners,
                                        })}
                                    </Typography.Text>
                                </li>{" "}
                                <li
                                    className={clsx(
                                        "col-span-4 flex min-w-0 items-center gap-ant-xs",
                                        location ? "col-span-4" : "col-span-6",
                                    )}
                                >
                                    <CalendarIcon className="text-primary" />
                                    <Typography.Text className="font-ant-strong">
                                        {t("page.classrooms.home.classroom.dates", {
                                            startDate: new Date(startDate),
                                            endDate: new Date(endDate),
                                        })}
                                    </Typography.Text>
                                </li>
                            </ul>
                            <div className="mt-ant-lg flex justify-between">
                                <div>
                                    <FeatureGuard feature={Feature.CLASSROOM_UPDATE}>
                                        <Link
                                            href={interpolate(Route.CLASSROOMS_EDIT, {
                                                classroomId: getResourceUuid(id),
                                            })}
                                            passHref
                                        >
                                            <Button
                                                color="primary"
                                                variant="outlined"
                                                title={t("component.classroomCard.editButton.title", {
                                                    classroomTitle: title,
                                                })}
                                            >
                                                {t("component.classroomCard.editButton.text")}
                                                <Pencil />
                                            </Button>
                                        </Link>
                                    </FeatureGuard>
                                </div>
                                <FeatureGuard feature={Feature.CLASSROOM_WEBINAR}>
                                    {showVisio && isClassroomWebinarAvailable(classroom) ? (
                                        <OpenClassroomWebinarButton classroom={classroom["@id"]} />
                                    ) : null}
                                </FeatureGuard>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClassroomCardContent;
